import React, { useState, useEffect, useMemo } from 'react'
import { Alert, Badge, Box, InputAdornment, Tab, Tabs, TextField, Typography } from '@mui/material'
import Search from '@mui/icons-material/Search'
import ServiceApprovalsList from './components/ServiceApprovalsList'
import DisapprovalApprovalDialog from './components/ApprovalDialog'
import { useApprovalDialog as useApprovalDisapprovalDialog } from './hooks/useApprovalDialog'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { getUserRoles } from 'src/mentor/layout/utils'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { toast } from 'react-toastify'
import { findApprovalTypeFromApprovalRole } from 'src/supportPortal/views/serviceApprovals/utils/approvalUtils'
import {
  SERVICE_APPROVAL_TAB_ACCESS,
  formatSupportRoleNames,
  hasAnySupportRole,
} from 'src/supportPortal/access/accessMatrix'

export const SUPPORT_APPROVALS_LISTING_CONFIG = [
  {
    id: 'doctors',
    label: 'Doctors',
    approvalRole: 'ROLE_DOCTOR',
    api: '/api/secure/cc/support/doctor/services-approved/list',
    practitionerType: 'doctor',
    reportType: 'prescription',
  },
  {
    id: 'therapists',
    label: 'Therapist',
    approvalRole: 'ROLE_THERAPIST',
    api: '/api/secure/cc/support/service-approvals/therapists/list',
    practitionerType: 'therapist',
    reportType: 'therapy-report',
  },
  {
    id: 'screeners',
    label: 'Screeners',
    approvalRole: 'ROLE_SCREENER',
    api: '/api/secure/cc/support/service-approvals/screeners/list',
    practitionerType: 'screener',
    reportType: 'screening-report',
  },
  {
    id: 'contentCreators',
    label: 'Content Creators',
    api: '/api/secure/cc/support/service-approvals/unapproved/content-creators/list',
    actionsEnabled: false,
  },
  {
    id: 'unapproved',
    label: '1-on-1 Unapproved',
    api: '/api/secure/cc/support/service-approvals/unapproved/one-on-one/list',
  },
]

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

export default function ServiceApprovalsListing({ reportType }) {
  // Phase 14bj — userRoles MUST be memoised once on mount. Without this, the page
  // hits an infinite-render loop and the browser eventually fires every backend
  // service-approvals endpoint hundreds of times until ERR_INSUFFICIENT_RESOURCES
  // (a real prod report from /support-home/practitioner-management/service-approvals).
  // The cycle:
  //   1. `getUserRoles()` calls JSON.parse(localStorage.authorities), which creates
  //      a NEW array on every call.
  //   2. `userRoles` (used to be a plain `const userRoles = getUserRoles() || []`)
  //      was therefore a new reference on every render.
  //   3. `tabAccessMap` is a useMemo with `[userRoles]` deps — new ref → recompute.
  //   4. The useEffect that calls `fetchData()` has `[reportType, tabAccessMap]` deps
  //      — new tabAccessMap object → effect re-fires.
  //   5. `fetchData()` calls `setTabsData()` for each tab → re-render.
  //   6. Re-render → step 2.
  // Memoising once on mount fixes the cycle: authorities don't change without a
  // sign-out / reload, so an empty deps array gives the right semantics. The `|| []`
  // fallback survives the case where authorities were never written to localStorage.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const userRoles = useMemo(() => getUserRoles() || [], [])
  const [tabsData, setTabsData] = useState(
    SUPPORT_APPROVALS_LISTING_CONFIG.reduce((acc, tab) => {
      acc[tab.id] = { data: [], loading: true }
      return acc
    }, {}),
  )
  const [activeTab, setActiveTab] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [approvalRoles, setApprovalsRoles] = useState()
  const [specializations, setSpecializations] = useState([])
  const [targetSpecialties, setTargetSpecialties] = useState([])

  const { dialogState, openDialog, closeDialog, onSubmitAction } = useApprovalDisapprovalDialog({
    onSuccess: () => {
      fetchData()
      // TODO: Show success toast/snackbar
      console.log('Approval successful')
    },
    onError: (error) => {
      // TODO: Show error toast/snackbar
      console.error('Approval failed:', error)
      toast.error('Approval failed: ' + error.message)
    },
  })

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setSearchQuery('')
  }

  const updateTabData = (tabId, data) => {
    setTabsData((prev) => ({
      ...prev,
      [tabId]: { data, loading: false },
    }))
  }

  async function getApprovalTypes() {
    const resp = await getSPDataWithParams('/api/secure/cc/support/reference/approval-roles')
    if (resp.status === 200) {
      setApprovalsRoles(resp.data)
    }
  }

  async function getSpecializations() {
    const resp = await getSPDataWithParams('/api/secure/cc/support/categories')
    if (resp.status === 200) {
      setSpecializations(resp.data || [])
    }
  }

  async function getTargetSpecialties() {
    const resp = await getSPDataWithParams('/api/secure/cc/support/target-specialties', {
      includeDisabled: false,
      sortBy: 'title',
    })
    if (resp.status === 200) {
      setTargetSpecialties(resp.data || [])
    }
  }

  const filterPractitioners = (list) => {
    if (!searchQuery.trim()) return list
    return list.filter((practitioner) =>
      practitioner?.name?.toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }

  const filteredTabsData = useMemo(() => {
    return SUPPORT_APPROVALS_LISTING_CONFIG.reduce((acc, tab) => {
      acc[tab.id] = filterPractitioners(tabsData[tab.id]?.data || [])
      return acc
    }, {})
  }, [tabsData, searchQuery])

  const tabAccessMap = useMemo(
    () =>
      SUPPORT_APPROVALS_LISTING_CONFIG.reduce((acc, tab) => {
        acc[tab.id] = hasAnySupportRole(userRoles, SERVICE_APPROVAL_TAB_ACCESS[tab.id] || [])
        return acc
      }, {}),
    [userRoles],
  )

  const getTabRequiredRolesLabel = (tabId) =>
    formatSupportRoleNames(SERVICE_APPROVAL_TAB_ACCESS[tabId] || [])

  const fetchData = async () => {
    if (reportType) {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/service-approvals/unapproved/one-on-one/list',
      )
      if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)?.mentor || []
        updateTabData('therapists', data)
      }
    } else {
      SUPPORT_APPROVALS_LISTING_CONFIG.forEach((tab) => {
        if (!tabAccessMap[tab.id]) {
          updateTabData(tab.id, [])
          return
        }
        getSPDataWithParams(tab.api).then((resp) => {
          if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
            const data = getDataMapFromCommonResponse(resp)?.mentor || []
            updateTabData(tab.id, data)
          } else {
            updateTabData(tab.id, [])
          }
        })
      })
    }
  }

  useEffect(() => {
    fetchData()
  }, [reportType, tabAccessMap])

  useEffect(() => {
    getSpecializations()
    getTargetSpecialties()
    getApprovalTypes()
  }, [])

  // Report mode: Show only therapist listing without tabs
  if (reportType) {
    return (
      <>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              placeholder="Search therapists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>
          <ServiceApprovalsList
            practitioners={filteredTabsData.therapists}
            loading={tabsData.therapists?.loading}
            onOpenApprovalDisapprovalDialog={openDialog}
            approvalRoles={approvalRoles}
            sourceTabId="ROLE_THERAPIST"
            specializations={specializations}
            targetSpecialties={targetSpecialties}
            userRoles={userRoles}
          />
        </Box>
        <DisapprovalApprovalDialog
          open={dialogState.open}
          onClose={closeDialog}
          onSubmit={onSubmitAction}
          approvalDisapprovalRole={findApprovalTypeFromApprovalRole(dialogState.approvalRole, approvalRoles)}
          approvalActionType={dialogState.actionType}
          practitioner={dialogState.practitioner}
          loading={dialogState.loading}
          specializations={specializations}
          targetSpecialties={targetSpecialties}
        />
      </>
    )
  }

  // Normal mode: Show tabs with all lists
  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.default',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <SupportHeader heading="Service Approvals" />
        <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              {SUPPORT_APPROVALS_LISTING_CONFIG.map((tab) => {
                const foundApprovalType = approvalRoles?.find(
                  (approvalType) => approvalType.id === tab.approvalRole,
                )
                if (foundApprovalType) {
                  return { ...tab, label: foundApprovalType?.title }
                }
                return tab
              }).map((tab) => (
                <Tab
                  key={tab.id}
                  label={
                    <Badge badgeContent={tabsData[tab.id]?.data.length || 0} color="primary">
                      {tab.label}
                    </Badge>
                  }
                />
              ))}
            </Tabs>
          </Box>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: '100%' }}>
        {SUPPORT_APPROVALS_LISTING_CONFIG.map((tab, index) => (
          <TabPanel key={tab.id} value={activeTab} index={index}>
            {tabAccessMap[tab.id] ? (
              <ServiceApprovalsList
                practitioners={filteredTabsData[tab.id]}
                loading={tabsData[tab.id]?.loading}
                onOpenApprovalDisapprovalDialog={openDialog}
                approvalRoles={approvalRoles}
                actionsEnabled={tab.actionsEnabled !== false}
                sourceTabId={tab.approvalRole}
                sourceTabLabel={tab.label}
                specializations={specializations}
                targetSpecialties={targetSpecialties}
                userRoles={userRoles}
              />
            ) : (
              <Box sx={{ p: 2 }}>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {tab.label} can be accessed by: {getTabRequiredRolesLabel(tab.id)}
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  The tab remains visible so role requirements are clear, but the data and actions are available only to the listed roles.
                </Typography>
              </Box>
            )}
          </TabPanel>
        ))}
      </Box>
      <DisapprovalApprovalDialog
        open={dialogState.open}
        onClose={closeDialog}
        onSubmit={onSubmitAction}
        approvalDisapprovalRole={findApprovalTypeFromApprovalRole(dialogState.approvalRole, approvalRoles)}
        approvalActionType={dialogState.actionType}
        practitioner={dialogState.practitioner}
        loading={dialogState.loading}
        specializations={specializations}
        targetSpecialties={targetSpecialties}
      />
    </>
  )
}
